// =============================================================================
// Minister Showcase — Share talks, social media, newsletters & get feedback
// =============================================================================

import { useState, useCallback, useRef } from 'react';

// Types
interface ShowcaseItem {
  id: string;
  type: 'youtube' | 'social' | 'newsletter' | 'website' | 'podcast' | 'talk' | 'other';
  title: string;
  url: string;
  imageUrl: string; // URL or base64 data URI for representative image
  description: string;
  feedbackRequest: string;
  addedAt: string;
}

interface FeedbackEntry {
  id: string;
  author: string;
  role: string;
  text: string;
  createdAt: string;
}

interface ShowcaseSectionProps {
  data: Record<string, string>;
  onChange: (id: string, val: string) => void;
  role: string;
  userName: string;
}

// Helpers
const generateId = () => `sc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const TYPE_OPTIONS: { value: ShowcaseItem['type']; label: string; icon: string; hint: string }[] = [
  { value: 'youtube', label: 'YouTube Video', icon: '🎬', hint: 'A talk, sermon, lesson, or YouTube Short' },
  { value: 'talk', label: 'Talk / Sermon', icon: '🎙️', hint: 'Audio or video talk hosted elsewhere (Vimeo, podcast feed, etc.)' },
  { value: 'social', label: 'Social Media', icon: '📱', hint: 'Facebook, Instagram, TikTok, LinkedIn, X/Twitter profile or post' },
  { value: 'newsletter', label: 'Newsletter', icon: '📰', hint: 'Mailchimp, Substack, Constant Contact, or other newsletter' },
  { value: 'podcast', label: 'Podcast', icon: '🎧', hint: 'Spotify, Apple Podcasts, or podcast RSS link' },
  { value: 'website', label: 'Website', icon: '🌐', hint: 'Community website, personal website, or landing page' },
  { value: 'other', label: 'Other Resource', icon: '📎', hint: 'Any other link you want to share' },
];

const typeConfig = (type: string) => TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[TYPE_OPTIONS.length - 1];

// YouTube embed helpers
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function extractVimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

// Auto-detect thumbnail URL from the link itself
function getAutoThumbnail(url: string, type: string): string | null {
  const ytId = extractYouTubeId(url);
  if (ytId) return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
  // Vimeo requires an API call, skip for now
  return null;
}

// Resize an image file to a compressed base64 data URI (max 400x400, JPEG 0.7)
function resizeImageToDataUrl(file: File, maxDim = 400, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
          else { w = Math.round(w * maxDim / h); h = maxDim; }
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not available')); return; }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Hero image display for a showcase card
function HeroImage({ item }: { item: ShowcaseItem }) {
  const imgSrc = item.imageUrl || getAutoThumbnail(item.url, item.type);
  if (!imgSrc) return null;

  const ytId = extractYouTubeId(item.url);
  const isAutoYt = !item.imageUrl && !!ytId;

  return (
    <div style={{
      position: 'relative', borderRadius: 14, overflow: 'hidden',
      marginBottom: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: '#1a1025',
    }}>
      <img
        src={imgSrc}
        alt={item.title}
        style={{
          width: '100%', display: 'block',
          maxHeight: isAutoYt ? undefined : 320,
          objectFit: 'cover',
        }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
      {/* Gradient overlay at bottom for readability */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.4))',
        pointerEvents: 'none',
      }} />
      {/* Type badge on image */}
      <div style={{
        position: 'absolute', top: 10, left: 10,
        padding: '4px 12px', borderRadius: 10,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
        fontSize: 11, fontWeight: 600, color: '#fff',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {typeConfig(item.type).icon} {typeConfig(item.type).label}
      </div>
    </div>
  );
}

function EmbedPreview({ url, type, hasHeroImage }: { url: string; type: string; hasHeroImage: boolean }) {
  const ytId = extractYouTubeId(url);
  const vimeoId = extractVimeoId(url);

  // If there's a hero image and it's YouTube, show the embed instead (so they can watch inline)
  if (ytId) {
    return (
      <div style={{ position: 'relative', paddingBottom: '56.25%', borderRadius: 14, overflow: 'hidden', marginBottom: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <iframe
          src={`https://www.youtube.com/embed/${ytId}`}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Embedded video"
        />
      </div>
    );
  }

  if (vimeoId) {
    return (
      <div style={{ position: 'relative', paddingBottom: '56.25%', borderRadius: 14, overflow: 'hidden', marginBottom: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <iframe
          src={`https://player.vimeo.com/video/${vimeoId}`}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Embedded video"
        />
      </div>
    );
  }

  // For non-embeddable links without a hero image, show a styled link card
  if (hasHeroImage) return null; // Hero image already covers it visually

  const tc = typeConfig(type);
  let domain = '';
  try { domain = new URL(url).hostname.replace('www.', ''); } catch { domain = url; }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px', borderRadius: 12, marginBottom: 12,
      background: 'linear-gradient(135deg, rgba(124,77,186,0.04), rgba(155,114,207,0.08))',
      border: '1px solid rgba(124,77,186,0.12)', textDecoration: 'none',
      transition: 'all 0.2s',
    }}>
      <span style={{ fontSize: 24 }}>{tc.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#7c4dba', fontWeight: 600 }}>{domain}</div>
        <div style={{ fontSize: 11, color: '#9a8ab0' }}>Click to open in a new tab ↗</div>
      </div>
    </a>
  );
}

// Styles
const cardStyle: React.CSSProperties = {
  padding: '24px 28px', borderRadius: 22, marginBottom: 16,
  background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(12px)',
  border: '1px solid rgba(180,160,210,0.15)',
  boxShadow: '0 4px 24px rgba(100,60,140,0.06)',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 24px', borderRadius: 20, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(135deg, #7c4dba, #9b72cf)', color: '#fff',
  fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
  boxShadow: '0 3px 10px rgba(124,77,186,0.25)',
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 16, cursor: 'pointer',
  border: '1.5px solid rgba(124,77,186,0.2)',
  background: 'rgba(255,255,255,0.6)', color: '#7c4dba',
  fontSize: 12, fontWeight: 500, transition: 'all 0.2s',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: 12,
  border: '1.5px solid rgba(180,160,210,0.3)', background: 'rgba(255,255,255,0.7)',
  fontSize: 14, color: '#3a2a50', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontWeight: 600, fontSize: 13,
  color: '#3a2a50', marginBottom: 6,
};

// ============================================================================
// Main Component
// ============================================================================

export default function ShowcaseSection({ data, onChange, role, userName }: ShowcaseSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  // Parse showcase items from report data
  const getItems = useCallback((): ShowcaseItem[] => {
    try { return JSON.parse(data.showcaseItems || '[]'); }
    catch { return []; }
  }, [data.showcaseItems]);

  const getFeedback = useCallback((): Record<string, FeedbackEntry[]> => {
    try { return JSON.parse(data.showcaseFeedback || '{}'); }
    catch { return {}; }
  }, [data.showcaseFeedback]);

  const saveItems = (items: ShowcaseItem[]) => {
    onChange('showcaseItems', JSON.stringify(items));
  };

  const saveFeedback = (fb: Record<string, FeedbackEntry[]>) => {
    onChange('showcaseFeedback', JSON.stringify(fb));
  };

  const handleAddItem = (item: Omit<ShowcaseItem, 'id' | 'addedAt'>) => {
    const items = getItems();
    items.unshift({
      ...item,
      id: generateId(),
      addedAt: new Date().toISOString(),
    });
    saveItems(items);
    setShowAddForm(false);
  };

  const handleUpdateItem = (itemId: string, updates: Omit<ShowcaseItem, 'id' | 'addedAt'>) => {
    const items = getItems().map(i =>
      i.id === itemId ? { ...i, ...updates } : i
    );
    saveItems(items);
    setEditingId(null);
  };

  const handleDeleteItem = (itemId: string) => {
    if (!confirm('Remove this item from your showcase?')) return;
    const items = getItems().filter(i => i.id !== itemId);
    saveItems(items);
    // Also remove feedback for this item
    const fb = getFeedback();
    delete fb[itemId];
    saveFeedback(fb);
  };

  const handleAddFeedback = (itemId: string) => {
    if (!replyText.trim()) return;
    const fb = getFeedback();
    if (!fb[itemId]) fb[itemId] = [];
    fb[itemId].push({
      id: generateId(),
      author: userName,
      role,
      text: replyText.trim(),
      createdAt: new Date().toISOString(),
    });
    saveFeedback(fb);
    setReplyText('');
    setReplyingTo(null);
  };

  const handleDeleteFeedback = (itemId: string, feedbackId: string) => {
    const fb = getFeedback();
    if (fb[itemId]) {
      fb[itemId] = fb[itemId].filter(f => f.id !== feedbackId);
      saveFeedback(fb);
    }
  };

  const items = getItems();
  const feedback = getFeedback();

  return (
    <div>
      {/* Header */}
      <div style={{ ...cardStyle, background: 'linear-gradient(135deg, rgba(124,77,186,0.06), rgba(200,160,240,0.10))' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: '0 0 6px', color: '#3a2a50', fontSize: 20, fontFamily: "'Georgia', serif" }}>
              ✨ Minister Showcase
            </h3>
            <p style={{ margin: 0, color: '#7a6a8a', fontSize: 13, lineHeight: 1.6, maxWidth: 520 }}>
              {role === 'minister'
                ? 'Share your talks, social media, newsletters, and online presence with your sponsor and mentor. They can watch, read, and leave feedback right here — no more sending links over email!'
                : 'Explore the minister\'s talks, social media, and online presence. Leave feedback and encouragement directly on each item.'}
            </p>
          </div>
          {role === 'minister' && (
            <button onClick={() => setShowAddForm(!showAddForm)} style={btnPrimary}>
              {showAddForm ? '✕ Cancel' : '+ Share Something'}
            </button>
          )}
        </div>

        {/* Quick stats */}
        {items.length > 0 && (
          <div style={{
            display: 'flex', gap: 16, marginTop: 16, paddingTop: 16,
            borderTop: '1px solid rgba(180,160,210,0.15)', flexWrap: 'wrap',
          }}>
            {TYPE_OPTIONS.map(t => {
              const count = items.filter(i => i.type === t.value).length;
              if (count === 0) return null;
              return (
                <span key={t.value} style={{
                  padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                  background: 'rgba(124,77,186,0.06)', color: '#7c4dba',
                }}>
                  {t.icon} {count} {t.label}{count > 1 ? 's' : ''}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && role === 'minister' && (
        <AddItemForm onAdd={handleAddItem} onCancel={() => setShowAddForm(false)} />
      )}

      {/* Items */}
      {items.length === 0 && !showAddForm ? (
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎬</div>
          <p style={{ color: '#7a6a8a', fontSize: 15, margin: '0 0 8px' }}>
            {role === 'minister' ? 'No items shared yet' : 'The minister hasn\'t shared anything yet'}
          </p>
          <p style={{ color: '#9a8ab0', fontSize: 13, margin: 0 }}>
            {role === 'minister' ? 'Share a talk, social media profile, newsletter, or any link you\'d like your sponsor and mentor to see.' : 'Check back later for talks, social media links, and other resources.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {items.map(item => {
            const tc = typeConfig(item.type);
            const itemFeedback = feedback[item.id] || [];
            const isReplying = replyingTo === item.id;
            const isEditing = editingId === item.id;
            const hasHeroImage = !!(item.imageUrl || getAutoThumbnail(item.url, item.type));
            const isEmbeddable = !!extractYouTubeId(item.url) || !!extractVimeoId(item.url);

            // Render inline edit form
            if (isEditing && role === 'minister') {
              return (
                <EditItemForm
                  key={item.id}
                  item={item}
                  onSave={(updates) => handleUpdateItem(item.id, updates)}
                  onCancel={() => setEditingId(null)}
                />
              );
            }

            return (
              <div key={item.id} style={cardStyle}>
                {/* Item header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: 'linear-gradient(135deg, rgba(124,77,186,0.1), rgba(155,114,207,0.15))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                    }}>
                      {tc.icon}
                    </span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#3a2a50' }}>{item.title}</div>
                      <div style={{ fontSize: 11, color: '#9a8ab0' }}>
                        {tc.label} · Added {new Date(item.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {!isEmbeddable && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
                        ...btnSecondary, padding: '5px 12px', fontSize: 11, textDecoration: 'none',
                      }}>Open ↗</a>
                    )}
                    {role === 'minister' && (
                      <button onClick={() => setEditingId(item.id)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#9a8ab0', fontSize: 14, padding: 4, transition: 'color 0.2s',
                      }} title="Edit item">✏️</button>
                    )}
                    {role === 'minister' && (
                      <button onClick={() => handleDeleteItem(item.id)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#c4b0d6', fontSize: 16, padding: 4, transition: 'color 0.2s',
                      }} title="Remove item">✕</button>
                    )}
                  </div>
                </div>

                {/* Hero image (uploaded or auto-detected) */}
                {!isEmbeddable && <HeroImage item={item} />}

                {/* Embed / Preview (YouTube/Vimeo get iframe, others get link card) */}
                <EmbedPreview url={item.url} type={item.type} hasHeroImage={hasHeroImage} />

                {/* Description */}
                {item.description && (
                  <p style={{ margin: '0 0 12px', fontSize: 14, color: '#4a3660', lineHeight: 1.6 }}>
                    {item.description}
                  </p>
                )}

                {/* Feedback request */}
                {item.feedbackRequest && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 12, marginBottom: 12,
                    background: 'linear-gradient(135deg, rgba(37,99,235,0.04), rgba(59,130,246,0.08))',
                    border: '1px solid rgba(37,99,235,0.12)',
                    fontSize: 13, color: '#2563eb',
                  }}>
                    <span style={{ fontWeight: 600 }}>💬 Feedback requested:</span> {item.feedbackRequest}
                  </div>
                )}

                {/* Feedback thread */}
                {itemFeedback.length > 0 && (
                  <div style={{
                    borderTop: '1px solid rgba(180,160,210,0.12)',
                    paddingTop: 12, marginTop: 4,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#7a6a8a', marginBottom: 8 }}>
                      💬 Feedback ({itemFeedback.length})
                    </div>
                    {itemFeedback.map(fb => (
                      <div key={fb.id} style={{
                        padding: '10px 14px', borderRadius: 12, marginBottom: 8,
                        background: fb.role === 'minister' ? 'rgba(124,77,186,0.04)' : fb.role === 'sponsor' ? 'rgba(245,158,11,0.04)' : 'rgba(5,150,105,0.04)',
                        border: `1px solid ${fb.role === 'minister' ? 'rgba(124,77,186,0.1)' : fb.role === 'sponsor' ? 'rgba(245,158,11,0.1)' : 'rgba(5,150,101,0.1)'}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#4a3660' }}>
                            {fb.author} <span style={{
                              padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600, marginLeft: 4,
                              background: fb.role === 'minister' ? 'rgba(124,77,186,0.08)' : fb.role === 'sponsor' ? 'rgba(245,158,11,0.08)' : 'rgba(5,150,101,0.08)',
                              color: fb.role === 'minister' ? '#7c4dba' : fb.role === 'sponsor' ? '#b45309' : '#059669',
                            }}>{fb.role}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: '#9a8ab0' }}>
                              {new Date(fb.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            {fb.role === role && (
                              <button onClick={() => handleDeleteFeedback(item.id, fb.id)} style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#c4b0d6', fontSize: 12, padding: 2,
                              }} title="Delete">✕</button>
                            )}
                          </div>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: '#4a3660', lineHeight: 1.5 }}>{fb.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply area */}
                {isReplying ? (
                  <div style={{ marginTop: 8 }}>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder={role === 'minister' ? 'Add a note or reply...' : 'Share your feedback, encouragement, or questions...'}
                      style={{
                        ...inputStyle, resize: 'vertical', minHeight: 80,
                        fontSize: 13, marginBottom: 8,
                      }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleAddFeedback(item.id)} disabled={!replyText.trim()} style={{
                        ...btnPrimary, padding: '8px 18px', fontSize: 12,
                        opacity: replyText.trim() ? 1 : 0.5,
                      }}>
                        Send Feedback
                      </button>
                      <button onClick={() => { setReplyingTo(null); setReplyText(''); }} style={{ ...btnSecondary, padding: '8px 14px' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setReplyingTo(item.id)} style={{
                    ...btnSecondary, marginTop: 8, padding: '6px 14px', fontSize: 12,
                  }}>
                    💬 {role === 'minister' ? 'Add Note' : 'Leave Feedback'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Add Item Form
// ============================================================================

function AddItemForm({ onAdd, onCancel }: {
  onAdd: (item: Omit<ShowcaseItem, 'id' | 'addedAt'>) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    type: 'youtube' as ShowcaseItem['type'],
    title: '',
    url: '',
    imageUrl: '',
    description: '',
    feedbackRequest: '',
  });
  const [imageMode, setImageMode] = useState<'none' | 'url' | 'upload'>('none');
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedType = typeConfig(form.type);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPEG, PNG, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be under 10 MB');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 600, 0.75);
      setForm(f => ({ ...f, imageUrl: dataUrl }));
      setUploadPreview(dataUrl);
    } catch (err) {
      alert('Failed to process image. Please try another file.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!form.title || !form.url) return;
    // Auto-prepend https:// if missing
    let url = form.url;
    if (url && !url.match(/^https?:\/\//)) url = 'https://' + url;
    onAdd({ ...form, url });
  };

  // Determine if auto-thumbnail is available
  const autoThumb = form.url ? getAutoThumbnail(form.url, form.type) : null;
  const displayImage = form.imageUrl || autoThumb;

  return (
    <div style={{
      ...cardStyle,
      background: 'linear-gradient(135deg, rgba(124,77,186,0.03), rgba(200,170,240,0.06))',
      border: '1.5px solid rgba(124,77,186,0.15)',
    }}>
      <h4 style={{ margin: '0 0 20px', color: '#3a2a50', fontSize: 17, fontFamily: "'Georgia', serif" }}>
        Share Something New
      </h4>

      {/* Type selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>What are you sharing?</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TYPE_OPTIONS.map(t => (
            <button key={t.value} onClick={() => setForm(f => ({ ...f, type: t.value }))} style={{
              padding: '8px 16px', borderRadius: 14,
              border: form.type === t.value ? '2px solid #7c4dba' : '1.5px solid rgba(180,160,210,0.2)',
              background: form.type === t.value ? 'rgba(124,77,186,0.08)' : 'rgba(255,255,255,0.5)',
              color: form.type === t.value ? '#7c4dba' : '#5a4a6a',
              fontSize: 12, fontWeight: form.type === t.value ? 600 : 500,
              cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9a8ab0', fontStyle: 'italic' }}>
          {selectedType.hint}
        </p>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Title *</label>
        <input
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder={form.type === 'youtube' ? 'e.g., Sunday Talk — "Living from Wholeness"' : form.type === 'social' ? 'e.g., Community Instagram Page' : 'Give this a name'}
          style={inputStyle}
        />
      </div>

      {/* URL */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Link / URL *</label>
        <input
          value={form.url}
          onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
          placeholder={form.type === 'youtube' ? 'https://youtube.com/watch?v=...' : form.type === 'social' ? 'https://instagram.com/your-page' : 'https://...'}
          style={inputStyle}
        />
        {/* Live preview for YouTube */}
        {form.url && extractYouTubeId(form.url) && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#059669', fontWeight: 600, marginBottom: 6 }}>✅ YouTube video detected — preview:</div>
            <EmbedPreview url={form.url} type={form.type} hasHeroImage={false} />
          </div>
        )}
      </div>

      {/* Cover Image */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Cover Image <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional — makes your showcase gorgeous)</span>
        </label>

        {/* Auto-thumbnail notification */}
        {autoThumb && imageMode === 'none' && !form.imageUrl && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px', borderRadius: 12, marginBottom: 10,
            background: 'rgba(5,150,105,0.04)', border: '1px solid rgba(5,150,105,0.1)',
          }}>
            <img src={autoThumb} alt="Auto thumbnail" style={{ width: 60, height: 45, borderRadius: 8, objectFit: 'cover' }} />
            <div>
              <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>✅ Thumbnail auto-detected</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Or upload your own image below</div>
            </div>
          </div>
        )}

        {/* Image mode selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button onClick={() => { setImageMode('upload'); }} style={{
            ...btnSecondary, padding: '8px 14px', fontSize: 12,
            ...(imageMode === 'upload' ? { borderColor: '#7c4dba', color: '#7c4dba', fontWeight: 600 } : {}),
          }}>📷 Upload Image</button>
          <button onClick={() => setImageMode('url')} style={{
            ...btnSecondary, padding: '8px 14px', fontSize: 12,
            ...(imageMode === 'url' ? { borderColor: '#7c4dba', color: '#7c4dba', fontWeight: 600 } : {}),
          }}>🔗 Image URL</button>
          {(form.imageUrl || imageMode !== 'none') && (
            <button onClick={() => {
              setImageMode('none');
              setForm(f => ({ ...f, imageUrl: '' }));
              setUploadPreview('');
            }} style={{ ...btnSecondary, padding: '8px 14px', fontSize: 12, color: '#9a8ab0' }}>
              ✕ Clear
            </button>
          )}
        </div>

        {/* Upload mode */}
        {imageMode === 'upload' && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{
              padding: '20px', borderRadius: 14, width: '100%',
              border: '2px dashed rgba(124,77,186,0.25)', cursor: 'pointer',
              background: 'rgba(124,77,186,0.02)', color: '#7c4dba',
              fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            }}>
              {uploading ? (
                <>⏳ Processing image...</>
              ) : uploadPreview ? (
                <>✅ Image uploaded — click to change</>
              ) : (
                <>
                  <span style={{ fontSize: 28 }}>📷</span>
                  Click to upload a cover image
                  <span style={{ fontSize: 11, color: '#9a8ab0' }}>JPEG, PNG, WebP — auto-resized for the web</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* URL mode */}
        {imageMode === 'url' && (
          <input
            value={form.imageUrl.startsWith('data:') ? '' : form.imageUrl}
            onChange={e => {
              setForm(f => ({ ...f, imageUrl: e.target.value }));
              setUploadPreview('');
            }}
            placeholder="https://example.com/podcast-artwork.jpg"
            style={inputStyle}
          />
        )}

        {/* Image preview */}
        {displayImage && (
          <div style={{ marginTop: 10, borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <img
              src={form.imageUrl || autoThumb || ''}
              alt="Cover preview"
              style={{ width: '100%', maxHeight: 250, objectFit: 'cover', display: 'block' }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      {/* Description */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Description <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional)</span></label>
        <textarea
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Tell your sponsor/mentor what this is about and why you're sharing it"
          style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
          rows={3}
        />
      </div>

      {/* Feedback request */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>What feedback would you like? <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional)</span></label>
        <textarea
          value={form.feedbackRequest}
          onChange={e => setForm(f => ({ ...f, feedbackRequest: e.target.value }))}
          placeholder="e.g., I'd love your thoughts on how I opened this talk, or whether the teaching point landed clearly"
          style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
          rows={2}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleSubmit} disabled={!form.title || !form.url} style={{
          ...btnPrimary, opacity: (form.title && form.url) ? 1 : 0.5,
        }}>
          {selectedType.icon} Add to Showcase
        </button>
        <button onClick={onCancel} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

// ============================================================================
// Edit Item Form — inline editing for existing showcase items
// ============================================================================

function EditItemForm({ item, onSave, onCancel }: {
  item: ShowcaseItem;
  onSave: (updates: Omit<ShowcaseItem, 'id' | 'addedAt'>) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    type: item.type,
    title: item.title,
    url: item.url,
    imageUrl: item.imageUrl || '',
    description: item.description,
    feedbackRequest: item.feedbackRequest,
  });
  const [imageMode, setImageMode] = useState<'none' | 'url' | 'upload'>(
    item.imageUrl ? (item.imageUrl.startsWith('data:') ? 'upload' : 'url') : 'none'
  );
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(item.imageUrl?.startsWith('data:') ? item.imageUrl : '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedType = typeConfig(form.type);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPEG, PNG, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be under 10 MB');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 600, 0.75);
      setForm(f => ({ ...f, imageUrl: dataUrl }));
      setUploadPreview(dataUrl);
    } catch {
      alert('Failed to process image. Please try another file.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!form.title || !form.url) return;
    let url = form.url;
    if (url && !url.match(/^https?:\/\//)) url = 'https://' + url;
    onSave({ ...form, url });
  };

  const autoThumb = form.url ? getAutoThumbnail(form.url, form.type) : null;
  const displayImage = form.imageUrl || autoThumb;

  return (
    <div style={{
      ...cardStyle,
      background: 'linear-gradient(135deg, rgba(37,99,235,0.03), rgba(59,130,246,0.06))',
      border: '1.5px solid rgba(37,99,235,0.15)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h4 style={{ margin: 0, color: '#2563eb', fontSize: 17, fontFamily: "'Georgia', serif" }}>
          ✏️ Edit Item
        </h4>
        <span style={{ fontSize: 11, color: '#9a8ab0' }}>
          Added {new Date(item.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      </div>

      {/* Type selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Type</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TYPE_OPTIONS.map(t => (
            <button key={t.value} onClick={() => setForm(f => ({ ...f, type: t.value }))} style={{
              padding: '8px 16px', borderRadius: 14,
              border: form.type === t.value ? '2px solid #7c4dba' : '1.5px solid rgba(180,160,210,0.2)',
              background: form.type === t.value ? 'rgba(124,77,186,0.08)' : 'rgba(255,255,255,0.5)',
              color: form.type === t.value ? '#7c4dba' : '#5a4a6a',
              fontSize: 12, fontWeight: form.type === t.value ? 600 : 500,
              cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Title *</label>
        <input
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          style={inputStyle}
        />
      </div>

      {/* URL */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Link / URL *</label>
        <input
          value={form.url}
          onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
          style={inputStyle}
        />
      </div>

      {/* Cover Image */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Cover Image <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional)</span>
        </label>

        {autoThumb && imageMode === 'none' && !form.imageUrl && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px', borderRadius: 12, marginBottom: 10,
            background: 'rgba(5,150,105,0.04)', border: '1px solid rgba(5,150,105,0.1)',
          }}>
            <img src={autoThumb} alt="Auto thumbnail" style={{ width: 60, height: 45, borderRadius: 8, objectFit: 'cover' }} />
            <div>
              <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>✅ Thumbnail auto-detected</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Or upload your own image below</div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button onClick={() => setImageMode('upload')} style={{
            ...btnSecondary, padding: '8px 14px', fontSize: 12,
            ...(imageMode === 'upload' ? { borderColor: '#7c4dba', color: '#7c4dba', fontWeight: 600 } : {}),
          }}>📷 Upload Image</button>
          <button onClick={() => setImageMode('url')} style={{
            ...btnSecondary, padding: '8px 14px', fontSize: 12,
            ...(imageMode === 'url' ? { borderColor: '#7c4dba', color: '#7c4dba', fontWeight: 600 } : {}),
          }}>🔗 Image URL</button>
          {(form.imageUrl || imageMode !== 'none') && (
            <button onClick={() => {
              setImageMode('none');
              setForm(f => ({ ...f, imageUrl: '' }));
              setUploadPreview('');
            }} style={{ ...btnSecondary, padding: '8px 14px', fontSize: 12, color: '#9a8ab0' }}>
              ✕ Clear
            </button>
          )}
        </div>

        {imageMode === 'upload' && (
          <div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{
              padding: '20px', borderRadius: 14, width: '100%',
              border: '2px dashed rgba(124,77,186,0.25)', cursor: 'pointer',
              background: 'rgba(124,77,186,0.02)', color: '#7c4dba',
              fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            }}>
              {uploading ? <>⏳ Processing image...</> : uploadPreview ? <>✅ Image uploaded — click to change</> : (
                <><span style={{ fontSize: 28 }}>📷</span>Click to upload a cover image</>
              )}
            </button>
          </div>
        )}

        {imageMode === 'url' && (
          <input
            value={form.imageUrl.startsWith('data:') ? '' : form.imageUrl}
            onChange={e => { setForm(f => ({ ...f, imageUrl: e.target.value })); setUploadPreview(''); }}
            placeholder="https://example.com/image.jpg"
            style={inputStyle}
          />
        )}

        {displayImage && (
          <div style={{ marginTop: 10, borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <img
              src={form.imageUrl || autoThumb || ''}
              alt="Cover preview"
              style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      {/* Description */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Description <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional)</span></label>
        <textarea
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
          rows={3}
        />
      </div>

      {/* Feedback request */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>What feedback would you like? <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional)</span></label>
        <textarea
          value={form.feedbackRequest}
          onChange={e => setForm(f => ({ ...f, feedbackRequest: e.target.value }))}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
          rows={2}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleSubmit} disabled={!form.title || !form.url} style={{
          ...btnPrimary, opacity: (form.title && form.url) ? 1 : 0.5,
        }}>
          ✅ Save Changes
        </button>
        <button onClick={onCancel} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}
